import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, BarChart3, UserPlus, Flag, Settings as SettingsIcon, FileText, Coins, Globe } from "lucide-react";
import { toast } from "sonner";

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [grant, setGrant] = useState({ user_id: "", tokens: 20, bucket: "bonus" });
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const doGrant = async (uid) => {
    try { await api.post("/admin/grant-tokens", { user_id: uid, tokens: Number(grant.tokens), bucket: grant.bucket }); toast.success("Tokens granted."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!users) return <Skeleton className="h-64 w-full rounded-2xl" />;
  return (
    <div className="space-y-3" data-testid="admin-users-tab">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Grant amount:</span>
        <Input type="number" value={grant.tokens} onChange={(e) => setGrant({ ...grant, tokens: e.target.value })} className="w-24" data-testid="grant-amount" />
        <Select value={grant.bucket} onValueChange={(v) => setGrant({ ...grant, bucket: v })}>
          <SelectTrigger className="w-40" data-testid="grant-bucket"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="bonus">bonus</SelectItem><SelectItem value="purchased">purchased</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-accent text-left"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Plan</th><th className="p-3">Tokens</th><th className="p-3" /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`admin-user-${u.id}`}>
                <td className="p-3 font-medium">{u.name}</td><td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3"><span className="rounded bg-accent px-2 py-0.5 text-xs capitalize">{u.role}{u.admin_scope ? `:${u.admin_scope}` : ""}</span></td>
                <td className="p-3">{u.plan || "—"}</td>
                <td className="p-3 font-mono">{(u.token_balance || 0) + (u.monthly_balance || 0) + (u.purchased_balance || 0)}</td>
                <td className="p-3"><Button size="sm" variant="outline" onClick={() => doGrant(u.id)} data-testid={`grant-btn-${u.id}`}><Coins className="mr-1 h-3.5 w-3.5" />Grant</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => setA(null)); }, []);
  if (!a) return <Skeleton className="h-64 w-full rounded-2xl" />;
  const maxViews = Math.max(1, ...a.by_country.map((c) => c.views));
  const maxDay = Math.max(1, ...a.by_day.map((d) => d.views));
  return (
    <div className="space-y-5" data-testid="admin-analytics-tab">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Total pageviews</div><div className="mt-1 font-display text-2xl font-bold">{a.total_views}</div></div>
        <div className="rounded-2xl border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Countries seen</div><div className="mt-1 font-display text-2xl font-bold">{a.by_country.length}</div></div>
        <div className="rounded-2xl border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Top region</div><div className="mt-1 font-display text-2xl font-bold">{a.by_country[0]?.country || "—"}</div></div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h4 className="mb-3 font-display font-semibold">Traffic by country</h4>
          {a.by_country.length === 0 ? <p className="text-sm text-muted-foreground">No visits recorded yet.</p> : a.by_country.map((c) => (
            <div key={c.country} className="mb-2" data-testid={`traffic-country-${c.country}`}>
              <div className="flex justify-between text-sm"><span className="font-medium">{c.country}</span><span className="font-mono text-muted-foreground">{c.views}</span></div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-[#0B5D3B] dark:bg-[#25D366]" style={{ width: `${(c.views / maxViews) * 100}%` }} /></div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h4 className="mb-3 font-display font-semibold">Students by signup country</h4>
          {a.users_by_country.length === 0 ? <p className="text-sm text-muted-foreground">No students yet.</p> : a.users_by_country.map((c) => (
            <div key={c.country} className="flex justify-between border-b border-border py-2 text-sm last:border-0"><span>{c.country}</span><span className="font-mono">{c.count}</span></div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h4 className="mb-3 font-display font-semibold">Pageviews — last 14 days</h4>
        <div className="flex items-end gap-1" style={{ height: 120 }}>
          {a.by_day.map((d) => (
            <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end" title={`${d.date}: ${d.views}`}>
              <div className="w-full rounded-t bg-[#0B5D3B] dark:bg-[#25D366]" style={{ height: `${Math.max(6, (d.views / maxDay) * 92)}px` }} />
              <span className="mt-1 text-[9px] text-muted-foreground">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
        {a.by_day.length === 0 && <p className="text-sm text-muted-foreground">No visits recorded yet.</p>}
      </div>
      <p className="text-xs text-muted-foreground">Region is detected from Cloudflare's country header (CF-IPCountry) on your live domain. In preview it may show "XX" until traffic arrives through Cloudflare.</p>
    </div>
  );
}

function UsageTab() {
  const [u, setU] = useState(null);
  useEffect(() => { api.get("/admin/usage").then(({ data }) => setU(data)).catch(() => setU(null)); }, []);
  if (!u) return <Skeleton className="h-48 w-full rounded-2xl" />;
  const stats = [
    { label: "Users", value: u.users }, { label: "Students", value: u.students },
    { label: "Assignments", value: u.assignments }, { label: "Evaluations", value: u.evaluations },
    { label: "Paid transactions", value: u.paid_transactions }, { label: "Revenue (USD)", value: `$${u.revenue_usd}` },
  ];
  return (
    <div className="space-y-5" data-testid="admin-usage-tab">
      <div className="grid gap-4 sm:grid-cols-3">
        {stats.map((s) => (<div key={s.label} className="rounded-2xl border border-border bg-card p-5"><div className="text-sm text-muted-foreground">{s.label}</div><div className="mt-1 font-display text-2xl font-bold">{s.value}</div></div>))}
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h4 className="mb-3 font-semibold">AI actions (tokens spent)</h4>
        {u.actions.map((a) => (<div key={a.action} className="flex justify-between border-b border-border py-2 text-sm last:border-0"><span className="capitalize">{a.action}</span><span className="font-mono">{a.count}× · {a.tokens} tokens</span></div>))}
      </div>
    </div>
  );
}

function StaffTab({ isOwner }) {
  const [staff, setStaff] = useState({ name: "", email: "", scope: "support" });
  const [tutor, setTutor] = useState({ name: "", email: "" });
  const [result, setResult] = useState(null);
  const createStaff = async () => {
    try { const { data } = await api.post("/admin/staff", staff); setResult(data); toast.success("Staff account created."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const inviteTutor = async () => {
    try { const { data } = await api.post("/admin/tutors/invite", tutor); setResult(data); toast.success("Tutor invited."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid gap-5 sm:grid-cols-2" data-testid="admin-staff-tab">
      {isOwner && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <h4 className="font-display font-semibold">Create staff account</h4>
          <div className="mt-3 space-y-3">
            <Input placeholder="Name" value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} data-testid="staff-name" />
            <Input placeholder="Email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} data-testid="staff-email" />
            <Select value={staff.scope} onValueChange={(v) => setStaff({ ...staff, scope: v })}>
              <SelectTrigger data-testid="staff-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="support">support</SelectItem><SelectItem value="content">content</SelectItem></SelectContent>
            </Select>
            <Button onClick={createStaff} className="w-full bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="create-staff-btn">Create staff</Button>
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-6">
        <h4 className="font-display font-semibold">Invite tutor</h4>
        <div className="mt-3 space-y-3">
          <Input placeholder="Name" value={tutor.name} onChange={(e) => setTutor({ ...tutor, name: e.target.value })} data-testid="tutor-name" />
          <Input placeholder="Email" value={tutor.email} onChange={(e) => setTutor({ ...tutor, email: e.target.value })} data-testid="tutor-email" />
          <Button onClick={inviteTutor} className="w-full bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="invite-tutor-btn">Invite tutor</Button>
        </div>
      </div>
      {result && (
        <div className="rounded-2xl border border-[#059669]/40 bg-[#E8F5EE] p-5 text-sm dark:bg-emerald-950/50 sm:col-span-2" data-testid="staff-result">
          Account created for <strong>{result.email}</strong>. Temporary password: <code className="rounded bg-card px-2 py-0.5 font-mono">{result.temp_password}</code> — share it securely; they should change it after first login.
        </div>
      )}
    </div>
  );
}

function FlagsTab({ isOwner }) {
  const [flags, setFlags] = useState(null);
  useEffect(() => { api.get("/admin/feature-flags").then(({ data }) => setFlags(data.flags)).catch(() => setFlags({})); }, []);
  const save = async (next) => {
    setFlags(next);
    if (isOwner) { try { await api.put("/admin/feature-flags", { values: next }); toast.success("Flags saved."); } catch (e) { toast.error("Save failed."); } }
  };
  if (!flags) return <Skeleton className="h-48 w-full rounded-2xl" />;
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-flags-tab">
      {Object.entries(flags).map(([k, v]) => (
        <label key={k} className="flex items-center justify-between border-b border-border py-3 text-sm last:border-0">
          <span className="capitalize">{k.replace(/_/g, " ")}</span>
          <Switch checked={v} onCheckedChange={(nv) => save({ ...flags, [k]: nv })} disabled={!isOwner} data-testid={`flag-${k}`} />
        </label>
      ))}
      {!isOwner && <p className="mt-3 text-xs text-muted-foreground">Only the owner can change feature flags.</p>}
    </div>
  );
}

function SettingsTab({ isOwner }) {
  const [settings, setSettings] = useState(null);
  const [rawValues, setRawValues] = useState({});
  const [wa, setWa] = useState({ whatsapp_number: "", whatsapp_message: "" });
  const [notes, setNotes] = useState("");
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      const v = data.values || {};
      setRawValues(v);
      setSettings(JSON.stringify(v, null, 2));
      setWa({ whatsapp_number: v.whatsapp_number || "", whatsapp_message: v.whatsapp_message || "" });
    }).catch(() => setSettings("{}"));
    api.get("/admin/build-notes").then(({ data }) => setNotes(data.text || "")).catch(() => {});
  }, []);
  const saveWhatsApp = async () => {
    try {
      const merged = { ...rawValues, whatsapp_number: wa.whatsapp_number.replace(/[^0-9]/g, ""), whatsapp_message: wa.whatsapp_message };
      await api.put("/admin/settings", { values: merged });
      setRawValues(merged); setSettings(JSON.stringify(merged, null, 2));
      toast.success("WhatsApp settings saved.");
    } catch (e) { toast.error("Save failed."); }
  };
  const saveSettings = async () => {
    try { const v = JSON.parse(settings); await api.put("/admin/settings", { values: v }); setRawValues(v); toast.success("Settings saved."); }
    catch (e) { toast.error("Invalid JSON or save failed."); }
  };
  const saveNotes = async () => { try { await api.put("/admin/build-notes", { text: notes }); toast.success("Build notes saved."); } catch (e) { toast.error("Save failed."); } };
  if (settings === null) return <Skeleton className="h-48 w-full rounded-2xl" />;
  return (
    <div className="grid gap-5" data-testid="admin-settings-tab">
      <div className="rounded-2xl border border-border bg-card p-6">
        <Label className="font-semibold">Expert help — WhatsApp</Label>
        <p className="mb-3 mt-1 text-sm text-muted-foreground">Sets the "Get expert help on WhatsApp" button students see on each assignment. Enter the number with country code, digits only (e.g. 447911123456).</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs text-muted-foreground">WhatsApp number</Label><Input value={wa.whatsapp_number} onChange={(e) => setWa({ ...wa, whatsapp_number: e.target.value })} placeholder="447911123456" data-testid="whatsapp-number-input" className="mt-1" disabled={!isOwner} /></div>
          <div><Label className="text-xs text-muted-foreground">Pre-filled message</Label><Input value={wa.whatsapp_message} onChange={(e) => setWa({ ...wa, whatsapp_message: e.target.value })} placeholder="Hi ThinkInk — I'd like expert help…" data-testid="whatsapp-message-input" className="mt-1" disabled={!isOwner} /></div>
        </div>
        {isOwner && <Button onClick={saveWhatsApp} className="mt-3 bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="save-whatsapp-btn">Save WhatsApp settings</Button>}
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <Label className="font-semibold">Advanced settings (JSON)</Label>
        <Textarea rows={6} value={settings} onChange={(e) => setSettings(e.target.value)} className="mt-2 font-mono text-xs" disabled={!isOwner} data-testid="settings-json" />
        {isOwner && <Button onClick={saveSettings} className="mt-3 bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="save-settings-btn">Save settings</Button>}
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <Label className="font-semibold">Build notes</Label>
        <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2" disabled={!isOwner} data-testid="build-notes" placeholder="What was built, what's missing, what the owner must configure…" />
        {isOwner && <Button onClick={saveNotes} className="mt-3 bg-[#0B5D3B] text-white hover:bg-[#08472D]" data-testid="save-notes-btn">Save notes</Button>}
      </div>
    </div>
  );
}

export default function AdminPortal() {
  const { user } = useAuth();
  const isOwner = user?.admin_scope === "owner";
  return (
    <div className="space-y-6" data-testid="admin-portal">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Admin</h1>
        <p className="mt-1 text-muted-foreground">Signed in as {user?.admin_scope} admin.</p>
      </div>
      <Tabs defaultValue="users">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="users" data-testid="tab-users"><Users className="mr-1.5 h-4 w-4" />Users</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage"><BarChart3 className="mr-1.5 h-4 w-4" />Usage</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><Globe className="mr-1.5 h-4 w-4" />Analytics</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff"><UserPlus className="mr-1.5 h-4 w-4" />Staff</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags"><Flag className="mr-1.5 h-4 w-4" />Flags</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="mr-1.5 h-4 w-4" />Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-5"><UsersTab /></TabsContent>
        <TabsContent value="usage" className="mt-5"><UsageTab /></TabsContent>
        <TabsContent value="analytics" className="mt-5"><AnalyticsTab /></TabsContent>
        <TabsContent value="staff" className="mt-5"><StaffTab isOwner={isOwner} /></TabsContent>
        <TabsContent value="flags" className="mt-5"><FlagsTab isOwner={isOwner} /></TabsContent>
        <TabsContent value="settings" className="mt-5"><SettingsTab isOwner={isOwner} /></TabsContent>
      </Tabs>
    </div>
  );
}
